import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Model from '@tripian/model';
import { api } from '@tripian/core';
import ICombinedState from '../redux/model/ICombinedState';
import { changeFavorites, changeLoadingFavorites, saveError } from '../redux/action/trip';
import IFavoritePoi from '../models/IFavoritePoi';

const useFavorite = () => {
  const [loading, setLoading] = useState<{ add: number[]; delete: number[] }>({ add: [], delete: [] });
  const { tripHash, cityId, favorites, loadingFavorites } = useSelector((state: ICombinedState) => ({
    tripHash: state.trip?.reference?.trip_hash,
    cityId: state.trip.reference?.city.id,
    favorites: state.trip.favorites,
    loadingFavorites: state.trip.loading.favorites,
  }));
  const dispatch = useDispatch();

  // if (tripHash === undefined) console.warn('useFavorite called with undefined tripHash'); // eslint-disable-line no-console

  // const loadingFavoriteAdd = (poiId: number) => loading.add.includes(poiId);
  // const loadingFavoriteDelete = (poiId: number) => loading.delete.includes(poiId);
  const loadingFavoritePoi = useCallback((poiId: number) => loading.add.includes(poiId) || loading.delete.includes(poiId), [loading.add, loading.delete]);

  const favoritesFetch = useCallback((): Promise<IFavoritePoi[]> => {
    if (cityId === undefined) {
      // eslint-disable-next-line no-console
      console.warn('fetchFavorites called with undefined cityId');
      return Promise.resolve([]);
    }
    dispatch(changeLoadingFavorites(true));

    return api
      .favorites(cityId)
      .then((modelFavorites: Model.Favorite[]) => {
        // Empty favorites
        if (modelFavorites.length === 0) {
          dispatch(changeFavorites([]));
          return [];
        }

        const favoritesResult: IFavoritePoi[] = [];
        const modelFavoriteIds: number[] = modelFavorites.map((mf) => mf.poi_id);

        return api
          .pois(modelFavoriteIds)
          .then((modelPois: Model.Poi[]) => {
            modelFavorites.forEach((mf: Model.Favorite) => {
              const favPoi: Model.Poi | undefined = modelPois.find((mp) => mp.id === mf.poi_id);

              if (favPoi) {
                const favoritePoi: IFavoritePoi = {
                  trip_hash: mf.trip_hash,
                  city_id: mf.city_id,
                  id: mf.id,
                  poi_id: mf.poi_id,
                  poi: favPoi,
                };
                favoritesResult.push(favoritePoi);
              } else {
                // eslint-disable-next-line no-console
                console.warn('favoritesFetch poi result not found for ', mf.poi_id);
              }
            });

            dispatch(changeFavorites(favoritesResult));
            return favoritesResult;
          })

          .catch((favoritesFetchPoisError) => {
            throw favoritesFetchPoisError;
          });
      })

      .catch((favoritesFetchError) => {
        dispatch(saveError('favoritesFetch', 'Fetch Favorites', favoritesFetchError));
        throw favoritesFetchError;
      })
      .finally(() => {
        dispatch(changeLoadingFavorites(false));
      });
  }, [cityId, dispatch]);

  const favoriteAdd = useCallback(
    (poiId: number): Promise<Model.Favorite> => {
      setLoading((prevLoading) => ({ ...prevLoading, add: [...prevLoading.add, poiId] }));

      return api
        .favoriteAdd(tripHash || '', poiId)
        .then((favorite: Model.Favorite) => favoritesFetch().then(() => favorite))

        .catch((favoriteAddError) => {
          dispatch(saveError('favoriteAdd', 'Add Favorite', favoriteAddError));
          throw favoriteAddError;
        })
        .finally(() => {
          setLoading((prevLoading) => {
            const newState = { ...prevLoading, add: [...prevLoading.add] };
            const index = newState.add.indexOf(poiId);
            if (index > -1) newState.add.splice(index, 1);
            return newState;
          });
        });
    },
    [dispatch, favoritesFetch, tripHash],
  );

  const favoriteDelete = useCallback(
    (favoriteId: number, poiId: number): Promise<number> => {
      setLoading((prevLoading) => ({ ...prevLoading, delete: [...prevLoading.delete, poiId] }));

      return api
        .favoriteDelete(favoriteId)
        .then((deletedFavoriteId: number) => favoritesFetch().then(() => deletedFavoriteId))

        .catch((favoriteDeleteError) => {
          dispatch(saveError('favoriteDelete', 'Delete Favorite', favoriteDeleteError));
          throw favoriteDeleteError;
        })
        .finally(() => {
          setLoading((prevLoading) => {
            const newState = { ...prevLoading, delete: [...prevLoading.delete] };
            const index = newState.delete.indexOf(poiId);
            if (index > -1) newState.delete.splice(index, 1);
            return newState;
          });
        });
    },
    [dispatch, favoritesFetch],
  );

  useEffect(() => {
    if (favorites === undefined && loadingFavorites === false && cityId !== undefined) favoritesFetch();
  }, [favoritesFetch, favorites, loadingFavorites, cityId]);

  return { favorites, loadingFavorites, loadingFavoritePoi, favoritesFetch, favoriteAdd, favoriteDelete };
};

export default useFavorite;
